/**
 * MockExam detail page (指定した模擬試験の概要).
 * Displays one mock exam's overview, details, and history of past attempts.
 * Reads mockExamId from the route.
 */
import { Stack } from "@mui/material";
import { FC, useCallback } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { spacing } from "@/theme/variables";
import { useMockExamDetail } from "@/infrastructure/adapters/mock-exam";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { AppRoutes } from "@/routes/config";
import { OverviewSection } from "@/features/mock-exam/components/mock-exam/OverviewSection";
import { HistorySection } from "@/features/mock-exam/components/mock-exam/HistorySection";
import { clearMockExamCache } from "@/features/mock-exam/utils/mock-exam-cache";
import { LoadingArea } from "@/components/elements/Loading/Loading";

export const MockExamPage: FC = () => {
  const navigate = useNavigate();
  const { mockExamId: mockExamIdParam } = useParams<{ mockExamId: string }>();
  const mockExamId = Number(mockExamIdParam) || 0;

  const { isTrial, isLoading: isTrialLoading } = useTrialStatus();
  const { metadata, attempts, isLoading } = useMockExamDetail(mockExamId, {
    enabled: !isTrialLoading && !isTrial && mockExamId > 0,
  });

  const handleStartMockExam = useCallback(() => {
    clearMockExamCache();
    navigate(AppRoutes.mockExamQuiz.replace(":mockExamId", String(mockExamId)));
  }, [navigate, mockExamId]);

  if (!mockExamId) {
    return <Navigate to={AppRoutes.mockExam} replace />;
  }

  if (isTrialLoading || isLoading) {
    return <LoadingArea />;
  }

  if (isTrial) {
    return <Navigate to={AppRoutes.study} replace />;
  }

  return (
    <Stack
      sx={{
        flex: 1,
        py: spacing.sm,
        gap: spacing.md,
      }}
    >
      {/* Overview Section */}
      <OverviewSection metadata={metadata} onStartMockExam={handleStartMockExam} />

      {/* History Section */}
      {metadata.totalQuestions > 0 && <HistorySection attempts={attempts} />}
    </Stack>
  );
};

export default MockExamPage;
